import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOrganization } from "../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import { useAuthenticatedSession } from "../hooks/useAuthenticatedSession";
import { CenteredCardLayout } from "./CenteredCardLayout";

import rebootCloud from "/reboot-cloud.svg";

type AcceptState = "loading" | "success" | "error" | "no-invite";

const AcceptInviteContent = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const [acceptState, setAcceptState] = useState<AcceptState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();

  const { acceptInvite } = useOrganization({ id: organizationId });

  useEffect(() => {
    const doAccept = async () => {
      try {
        const { aborted } = await acceptInvite({});

        if (aborted) {
          setAcceptState("error");
          setErrorMessage(aborted.message || "Failed to accept invitation.");
          return;
        }

        setAcceptState("success");
        // Redirect to dashboard after a short delay.
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 2000);
      } catch (error) {
        console.error("Error accepting invite:", error);
        setAcceptState("error");
        setErrorMessage("An unexpected error occurred.");
      }
    };

    doAccept();
  }, [acceptInvite, navigate]);

  if (acceptState === "loading") {
    return (
      <Card>
        <CardHeader className="text-center">
          <img src={rebootCloud} className="h-8 mx-auto mb-4" />
          <CardTitle>Accepting Invitation</CardTitle>
          <CardDescription>Please wait...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (acceptState === "success") {
    return (
      <Card>
        <CardHeader className="text-center">
          <img src={rebootCloud} className="h-8 mx-auto mb-4" />
          <CardTitle>Welcome!</CardTitle>
          <CardDescription>
            You've successfully joined the organization.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-8 gap-4">
          <CheckCircle className="h-12 w-12 text-green-500" />
          <p className="text-muted-foreground">Redirecting to dashboard...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <img src={rebootCloud} className="h-8 mx-auto mb-4" />
        <CardTitle>Unable to Accept Invitation</CardTitle>
        <CardDescription>{errorMessage}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center py-8 gap-4">
        <XCircle className="h-12 w-12 text-destructive" />
        <Button onClick={() => navigate("/", { replace: true })}>
          Go to Dashboard
        </Button>
      </CardContent>
    </Card>
  );
};

const LoggedInAcceptInvite = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  return (
    <CenteredCardLayout>
      <AcceptInviteContent organizationId={organizationId} />
    </CenteredCardLayout>
  );
};

// Resolves the signed-in user for an invitation that names an
// organization. Signing in is only worth demanding once the link is
// known to be usable, so this is mounted only for a valid one.
const AuthenticatedAcceptInvite = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const session = useAuthenticatedSession();

  // Wait for session to be ready (a signed-out visitor is sent to
  // the login page by `useAuthenticatedSession`, and comes back
  // here after signing in).
  if (session == undefined) {
    return (
      <CenteredCardLayout>
        <Card>
          <CardHeader className="text-center">
            <img src={rebootCloud} className="h-8 mx-auto mb-4" />
            <CardTitle>Loading</CardTitle>
            <CardDescription>Verifying your session...</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </CardContent>
        </Card>
      </CenteredCardLayout>
    );
  }

  return <LoggedInAcceptInvite organizationId={organizationId} />;
};

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const organizationId = searchParams.get("org");

  // If no organization ID provided, redirect to home.
  if (!organizationId) {
    return (
      <CenteredCardLayout>
        <Card>
          <CardHeader className="text-center">
            <img src={rebootCloud} className="h-8 mx-auto mb-4" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>
              No organization specified in the invitation link.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-8">
            <Button onClick={() => navigate("/", { replace: true })}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </CenteredCardLayout>
    );
  }

  return <AuthenticatedAcceptInvite organizationId={organizationId} />;
}
